import { useState, useEffect, useMemo } from 'react';
import { Button, Card, CardContent, CardHeader, Dialog, IconButton, Loader, Notice, SearchInput } from '@origo/origo-ui';
import { ArrowsOutIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import DOMPurify from 'dompurify';
import {
    type KommunaltGalleriItem,
    type KommunaltGalleriInput,
} from '../../services/kommunaltGalleriService';
import { useKommunaltGalleriService } from '../../../../servicecontexts/KommunaltGalleriServiceContext';
import { KommunaltGalleriFormDialog } from './KommunaltGalleriFormDialog';
import styles from './KommunaltGalleriPanel.module.css';

export function KommunaltGalleriPanel() {
    const service = useKommunaltGalleriService();
    const [items, setItems] = useState<KommunaltGalleriItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [expandedItem, setExpandedItem] = useState<KommunaltGalleriItem | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<KommunaltGalleriItem | null>(null);
    const [formSaving, setFormSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [deleteConfirmItem, setDeleteConfirmItem] = useState<KommunaltGalleriItem | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!service.isConfigured) return;
        void loadItems();
    }, []);

    async function loadItems() {
        setLoading(true);
        setFetchError(null);
        try {
            setItems(await service.fetchKommunaltGalleri());
        } catch (e) {
            setFetchError((e as Error)?.message ?? 'Kunne ikke laste galleriet');
        } finally {
            setLoading(false);
        }
    }

    const query = searchQuery.trim().toLowerCase();
    const filteredItems = useMemo(
        () => (query ? items.filter((item) => item.tittel.toLowerCase().includes(query)) : items),
        [items, query],
    );

    function handleAddClick() {
        setEditingItem(null);
        setFormError(null);
        setFormOpen(true);
    }

    function handleEditClick(item: KommunaltGalleriItem) {
        setEditingItem(item);
        setFormError(null);
        setFormOpen(true);
    }

    async function handleFormSubmit(input: KommunaltGalleriInput) {
        setFormSaving(true);
        setFormError(null);
        try {
            if (editingItem) {
                const newBildeUrl = await service.updateKommunaltGalleriItem(
                    {
                        objectId: editingItem.objectId,
                        tittel: input.tittel,
                        beskrivelse: input.beskrivelse ?? null,
                    },
                    input.bildefil,
                );
                setItems((prev) =>
                    prev.map((it) =>
                        it.objectId === editingItem.objectId
                            ? {
                                ...it,
                                tittel: input.tittel,
                                beskrivelse: input.beskrivelse ?? null,
                                bilde_url: newBildeUrl?.blobUrl ?? it.bilde_url,
                                bilde_permanent_url: newBildeUrl?.permanentUrl ?? it.bilde_permanent_url,
                            }
                            : it,
                    ),
                );
            } else {
                const created = await service.addKommunaltGalleriItem(input);
                setItems((prev) => [created, ...prev]);
            }
            setFormOpen(false);
        } catch (e) {
            setFormError((e as Error)?.message ?? 'Kunne ikke lagre');
        } finally {
            setFormSaving(false);
        }
    }

    async function handleDeleteConfirm() {
        if (!deleteConfirmItem) return;
        setDeleting(true);
        try {
            await service.deleteKommunaltGalleriItem(deleteConfirmItem.objectId);
            setItems((prev) => prev.filter((it) => it.objectId !== deleteConfirmItem.objectId));
            setDeleteConfirmItem(null);
        } catch (e) {
            console.error('Sletting feilet:', e);
        } finally {
            setDeleting(false);
        }
    }

    if (!service.isConfigured) {
        return (
            <div className={styles.root}>
                <Notice variant="danger">
                    Kommunalt galleri er ikke tilgjengelig.
                </Notice>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={styles.centered}>
                <Loader />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            {fetchError && (
                <Notice variant="danger" className={styles.notice}>
                    {fetchError}
                </Notice>
            )}

            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.title}>Kommunalt galleri</h2>
                    <span className={styles.count}>
                        {query
                            ? `${filteredItems.length} av ${items.length}`
                            : `${items.length} illustrasjoner`}
                    </span>
                </div>
                <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<PlusIcon size={16} />}
                    onClick={handleAddClick}
                >
                    Legg til
                </Button>
            </div>

            <div className={styles.searchBar}>
                <SearchInput
                    label="Søk"
                    hideLabel
                    placeholder="Filtrer etter navn..."
                    value={searchQuery}
                    onChange={setSearchQuery}
                />
            </div>

            <div className={styles.grid}>
                {filteredItems.length === 0 && (
                    <p className={styles.emptyMessage}>
                        {items.length === 0
                            ? 'Ingen illustrasjoner lagt til ennå.'
                            : 'Ingen illustrasjoner samsvarer med søket.'}
                    </p>
                )}
                {filteredItems.map((item) => (
                    <Card key={item.objectId}>
                        <div className={styles.cardImage}>
                            {item.bilde_url && <img src={item.bilde_url} alt={item.tittel} />}
                        </div>
                        <CardHeader className={styles.cardHeader}>
                            <p className={styles.cardTitle}>{item.tittel}</p>
                            <div className={styles.cardActions}>
                                <IconButton
                                    icon={<ArrowsOutIcon size={14} />}
                                    label="Forhåndsvis"
                                    title="Forhåndsvis"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setExpandedItem(item)}
                                />
                                <IconButton
                                    icon={<PencilSimpleIcon size={14} />}
                                    label="Rediger"
                                    title="Rediger"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditClick(item)}
                                />
                                <IconButton
                                    icon={<TrashIcon size={14} />}
                                    label="Slett"
                                    title="Slett"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteConfirmItem(item)}
                                />
                            </div>
                        </CardHeader>
                        {item.beskrivelse && (
                            <CardContent className={styles.cardContent}>
                                <p
                                    className={styles.cardDescription}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(item.beskrivelse),
                                    }}
                                />
                            </CardContent>
                        )}
                    </Card>
                ))}
            </div>

            <Dialog
                open={expandedItem !== null}
                onClose={() => setExpandedItem(null)}
                title={expandedItem?.tittel ?? ''}
                className={styles.detailDialog}
            >
                {expandedItem && (
                    <>
                        {expandedItem.bilde_url && (
                            <div className={styles.detailImage}>
                                <img src={expandedItem.bilde_url} alt={expandedItem.tittel} />
                            </div>
                        )}
                        {expandedItem.beskrivelse && (
                            <div
                                className={styles.detailDescription}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(expandedItem.beskrivelse),
                                }}
                            />
                        )}
                    </>
                )}
            </Dialog>

            <Dialog
                open={deleteConfirmItem !== null}
                onClose={() => setDeleteConfirmItem(null)}
                title="Slett illustrasjon"
                footer={
                    <div className={styles.dialogFooter}>
                        <Button
                            variant="secondary"
                            onClick={() => setDeleteConfirmItem(null)}
                            disabled={deleting}
                        >
                            Avbryt
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDeleteConfirm}
                            disabled={deleting}
                        >
                            {deleting ? 'Sletter...' : 'Slett'}
                        </Button>
                    </div>
                }
            >
                <p>Er du sikker på at du vil slette «{deleteConfirmItem?.tittel}»?</p>
            </Dialog>

            <KommunaltGalleriFormDialog
                open={formOpen}
                item={editingItem}
                saving={formSaving}
                error={formError}
                onClose={() => setFormOpen(false)}
                onSubmit={handleFormSubmit}
            />
        </div>
    );
}
